import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-camera-capture',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './camera-capture.component.html',
  styleUrls: ['./camera-capture.component.css']
})
export class CameraCaptureComponent implements OnChanges, OnDestroy {
  @Input() visible: boolean = false;
  @Output() captured = new EventEmitter<File>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('video') videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('playbackVideo') playbackVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  mediaStream: MediaStream | null = null;
  capturedImage: string | null = null;
  capturedVideoUrl: string | null = null;
  capturedVideoBlob: Blob | null = null;
  error: string | null = null;
  isLoading: boolean = false;

  // Video Recording State
  captureMode: 'photo' | 'video' = 'photo';
  currentFacingMode: 'user' | 'environment' = 'environment';
  isRecording: boolean = false;
  recordingTime: number = 0;
  private recordingTimer: any;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible']) {
      if (this.visible) {
        this.startCamera();
      } else {
        this.stopCamera();
        this.resetCapture();
      }
    }
  }

  ngOnDestroy() {
    this.stopCamera();
    this.resetCapture();
  }

  private resetCapture() {
    this.capturedImage = null;
    this.capturedVideoUrl = null;
    this.capturedVideoBlob = null;
    this.isRecording = false;
    this.recordingTime = 0;
    if (this.recordingTimer) clearInterval(this.recordingTimer);
  }

  async startCamera() {
    this.error = null;
    this.isLoading = true;
    this.resetCapture();

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: this.currentFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: this.captureMode === 'video' // Enable audio for video mode
      });

      if (this.videoRef && this.videoRef.nativeElement) {
        this.videoRef.nativeElement.srcObject = this.mediaStream;
      }
    } catch (err: any) {
      console.error('Camera access error:', err);
      this.error = 'Unable to access camera or microphone. Please check permissions.';
    } finally {
      this.isLoading = false;
    }
  }

  setMode(mode: 'photo' | 'video') {
    if (this.isRecording || this.capturedImage || this.capturedVideoUrl) return;
    this.captureMode = mode;
    this.startCamera(); // Restart camera to toggle audio track if needed
  }

  toggleCamera() {
    if (this.isRecording) return;
    this.currentFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
    this.startCamera();
  }

  stopCamera() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    if (this.videoRef && this.videoRef.nativeElement) {
      this.videoRef.nativeElement.srcObject = null;
    }
  }

  handleShutterClick() {
    if (this.captureMode === 'photo') {
      this.takeSnapshot();
    } else {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        this.startRecording();
      }
    }
  }

  takeSnapshot() {
    if (!this.videoRef || !this.canvasRef) return;

    const video = this.videoRef.nativeElement;
    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d');

    if (context) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      if (this.currentFacingMode === 'user') {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }
      
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      this.capturedImage = canvas.toDataURL('image/jpeg', 0.8);
      this.stopCamera();
    }
  }

  async startRecording() {
    if (!this.mediaStream) return;
    
    this.recordedChunks = [];
    try {
      this.mediaRecorder = new MediaRecorder(this.mediaStream, {
        mimeType: this.getSupportedMimeType()
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.capturedVideoBlob = new Blob(this.recordedChunks, { type: this.mediaRecorder?.mimeType });
        this.capturedVideoUrl = URL.createObjectURL(this.capturedVideoBlob);
      };

      this.mediaRecorder.start();
      this.isRecording = true;
      this.recordingTime = 0;
      this.recordingTimer = setInterval(() => {
        this.recordingTime++;
      }, 1000);
    } catch (err) {
      console.error('Recording start error:', err);
      this.error = 'Failed to start recording.';
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.isRecording = false;
      if (this.recordingTimer) clearInterval(this.recordingTimer);
      this.stopCamera();
    }
  }

  private getSupportedMimeType(): string {
    const types = ['video/mp4', 'video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return '';
  }

  getFormattedTime(): string {
    const mins = Math.floor(this.recordingTime / 60);
    const secs = this.recordingTime % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  retake() {
    this.capturedImage = null;
    this.capturedVideoUrl = null;
    this.capturedVideoBlob = null;
    this.startCamera();
  }

  useMedia() {
    if (this.captureMode === 'photo') {
      this.usePhoto();
    } else {
      this.useVideo();
    }
  }

  private usePhoto() {
    if (!this.capturedImage || !this.canvasRef) return;
    this.canvasRef.nativeElement.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        this.captured.emit(file);
        this.close();
      }
    }, 'image/jpeg', 0.8);
  }

  private useVideo() {
    if (!this.capturedVideoBlob) return;
    const extension = this.mediaRecorder?.mimeType.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([this.capturedVideoBlob], `video_${Date.now()}.${extension}`, { 
      type: this.mediaRecorder?.mimeType 
    });
    this.captured.emit(file);
    this.close();
  }

  close() {
    this.stopCamera();
    this.closed.emit();
  }
}
